import React, { useContext, useEffect } from "react";

import { AppContext } from "@/AppContext";
import { Breadcrumbs } from "@/ui/Breadcrumbs";

import { SidebarToggleIcon, DarkToggleIcon, LightToggleIcon } from "@/ui/Icons";

import { isEqual, truncate } from "lodash";
import { TopbarButtonProps, TopbarProps } from "./types";
import UniversalSearch from "@/ui/UniversalSearch";
import { Sparkle } from "@phosphor-icons/react";
import { loadChats } from "@/Apps/AITools/CopilotGallery";

export const TopbarButton: React.FC<TopbarButtonProps> = ({
  children = <></>,
  onClick = () => {},
  isActive = false,
}) => {
  return (
    <div
      className={`cursor-pointer p-2.5 rounded-lg ${
        isActive
          ? "hover:dark:bg-white/10 hover:bg-black/10 hover:dark:text-white hover:text-black dark:bg-white/90 bg-black/90 dark:text-black text-white"
          : "dark:bg-white/10 bg-black/10 dark:text-white text-black hover:dark:bg-white/90 hover:bg-black/90 hover:dark:text-black hover:text-white"
      }`}
      onClick={onClick}
    >
      {children}
    </div>
  );
};

export const Topbar: React.FC<TopbarProps> = ({
  showLeftSidebarToggle = false,
  page = [{ label: "Default", href: "/" }],
  items = null,
  centerItems = null,
  leftIcon = null,
  leftItems = null,
  hideCopilotButton = false,
  hideSearch = false,
  hideSidebarToggleButton = false,
  className = "px-5",
}) => {
  // Get the necessary context from the AppContext
  const {
    // darkMode,
    sidebarOpen,
    rightSidebarOpen,
    // setDarkMode,
    copilotOpen,
    copilotSettings,
    setCopilotOpen,
    setCopilotSettings,
    currentlyViewing,
    setSidebarOpen,
    setRightSidebarOpen,
    disableRightSidebar,
    setCurrentlyViewing,
    permissions,
  } = useContext(AppContext);

  // Toggle the left sidebar
  const toggleLeftSidebar = () => {
    localStorage.setItem("sidebarOpen", !sidebarOpen);
    setSidebarOpen(!sidebarOpen);
  };

  // Toggle the right sidebar
  const toggleRightSidebar = () => {
    localStorage.setItem("rightSidebarOpen", !rightSidebarOpen);
    setRightSidebarOpen(!rightSidebarOpen);
  };

  const toggleCopilot = async () => {
    if (copilotOpen) {
      setCopilotOpen(false);
    } else {
      setCopilotOpen(true);

      if (copilotSettings?.id) {
        // if the copilot is already open, set the settings to the current chat
        setCopilotSettings({ id: copilotSettings.id });
      } else {
        // load the first chat in the list
        await loadChats((seqs) => {
          // if the copilot is not open, load the first chat in the list
          if (seqs?.length > 0 && seqs[0]?.id) {
            setCopilotSettings({ id: seqs[0]?.id });
          }
        });
      }
    }
  };

  useEffect(() => {
    if (!isEqual(page, currentlyViewing)) {
      setCurrentlyViewing(page);
      console.log("Page updated", page, currentlyViewing);

    // Update the browser tab's title bar to reflect a stringified version of the breadcrumb labels
    if (Array.isArray(page) && page.length > 0) {
      const lastLabel = page.map(item => item.label).filter(Boolean).slice(-1)[0] || "";
      document.title = lastLabel;
    }

    }
  }, []);

  // Render the top bar
  return (
    <div
      data-testid="Topbar"
      className={`launch-topbar border-b border-black/10 dark:border-white/10 flex items-center justify-between ${className}`}
      style={{ height: 65 }}
    >
      <div className="flex items-center gap-2">
        {/* Toggle button for the left sidebar */}
        {/* {(!copilotOpen || showLeftSidebarToggle) && ( */}

        {!hideCopilotButton && (
          <div
            data-testid="Topbar-DarkModeToggle"
            className="flex items-center gap-2"
          >
            <TopbarButton
              data-testid="Topbar-RightSidebarToggle"
              onClick={toggleCopilot}
              isActive={copilotOpen}
            >
              <Sparkle className="text-lg" />
            </TopbarButton>
          </div>
        )}

        {leftIcon}

        {/* {!hideSidebarToggleButton && (
          <button
            type="button"
            className="text-black dark:text-white"
            onClick={toggleLeftSidebar}
            data-testid="Topbar-LeftSidebarToggle"
          >
            <SidebarToggleIcon />
          </button>
        )} */}

        {/* Breadcrumbs */}
        <Breadcrumbs
          items={page.map((item) => ({
            label: truncate(item.label, { length: 30 }),
            href: item.href,
            icon: item.icon,
            ...item,
          }))}
          type="Slash"
        />

        {leftItems}
      </div>

      {centerItems && (
        <div className="flex justify-center items-center">{centerItems}</div>
      )}

      <div className="flex items-center gap-3">
        {/* Universal search (⌘K "Ask Copilot"). Gated to the agents tier so the
            platform default is UNCHANGED — it was previously commented out for
            everyone. Re-enabling it platform-wide would surface a new control to
            non-agents customers (blast-radius / zero-change violation). Agent
            shells additionally pass hideSearch to opt out per-screen. */}
        {!hideSearch && permissions?.includes("agents") && <UniversalSearch />}

        {/* Action buttons */}
        {items}
      </div>
    </div>
  );
};
